/**
 * Developed by Spell Solutions for Trivinci Systems, LLC.
 *
 * Developer attribution only. Ownership, licensing, and permitted use are
 * governed by the applicable agreement and applicable law.
 */
import { LitElement, css, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { TelemetryClient } from "../services/telemetry";
import { getDriverName } from "../services/sessions";
import { getQuery, isTelemetrySource } from "../lib/url";
import type { TelemetrySource } from "../lib/url";
import "./rk-video";
import "./rk-hud";
import "./rk-panel";
import type { RkVideo } from "./rk-video";
import type { RkHud } from "./rk-hud";
import type { RkPanel } from "./rk-panel";

/**
 * <rk-player logger-id="...">
 *
 * Composite single-logger view.
 *  - Desktop: header | video (with HUD overlay) | right drawer
 *  - Mobile:  header overlay | full-bleed video | inline HUD at bottom; drawer hidden
 *
 * HUD dock:
 *  - "video" (default): HUD overlays the bottom of the video
 *  - "panel": HUD lives inside <rk-panel>; video overlay HUD is hidden
 *
 * One TelemetryClient drives both the video-overlay HUD and the panel.
 */

type HudDock = "video" | "panel";

@customElement("rk-player")
export class RkPlayer extends LitElement {
  static styles = css`
    :host {
      display: grid;
      grid-template-rows: auto 1fr;
      width: 100%;
      height: 100%;
      background: var(--rk-bg-0);
      overflow: hidden;
    }
    header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--rk-s-3);
      padding: 6px var(--rk-s-4);
      padding-top: max(6px, env(safe-area-inset-top));
      min-height: 0;
      border-bottom: 1px solid var(--rk-border);
      background: var(--rk-bg-glass-strong);
      backdrop-filter: blur(12px);
      z-index: 2;
    }
    /* Brand is now rendered ON the video (rk-video stage-logo). Header
       just carries driver + actions. */
    .brand-spacer { width: 1px; height: 1px; }
    .driver {
      font-family: var(--rk-font-mono);
      font-size: 11px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--rk-text-muted);
      max-width: 240px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .driver b {
      color: var(--rk-text);
      font-weight: 700;
      letter-spacing: -0.01em;
      text-transform: none;
      font-family: var(--rk-font-sans);
      margin-right: 8px;
    }
    .header-actions { display: flex; gap: 6px; }
    .header-actions a, .header-actions button {
      font-size: 11px;
      color: var(--rk-text-muted);
      text-decoration: none;
      padding: 3px 9px;
      border-radius: var(--rk-r-pill);
      border: 1px solid var(--rk-border);
      background: rgba(255, 255, 255, 0.03);
      cursor: pointer;
      transition: background var(--rk-dur-fast), color var(--rk-dur-fast);
    }
    .header-actions a:hover, .header-actions button:hover {
      color: var(--rk-text);
      background: rgba(255, 255, 255, 0.06);
    }

    .body {
      display: grid;
      grid-template-columns: 1fr auto;
      min-height: 0;
      position: relative;
    }
    .stage {
      position: relative;
      min-width: 0;
      padding: var(--rk-s-3);
    }
    /* In fullscreen the stage *is* the screen — drop padding & rounding
       so the video fills edge-to-edge, and paint the surround black so
       any letterbox area isn't desktop-colored. The HUD bottom-bar sits
       inside the stage, so it remains visible. */
    .stage:fullscreen,
    .stage:-webkit-full-screen {
      padding: 0;
      background: #000;
    }
    .stage:fullscreen rk-video,
    .stage:-webkit-full-screen rk-video {
      border-radius: 0;
    }
    rk-video {
      width: 100%;
      height: 100%;
      border-radius: var(--rk-r-lg);
    }
    /* Bottom bar — HUD flanked by two vertical control stacks. The whole
       row is centered as a flex container so the controls hug the HUD
       card from the outside instead of being trapped inside the video's
       stacking context. */
    .bottom-bar {
      position: absolute;
      left: 0;
      right: 0;
      bottom: calc(14px + env(safe-area-inset-bottom));
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 12px;
      padding: 0 14px;
      z-index: 4;
      pointer-events: none;
    }
    .bottom-bar rk-hud { pointer-events: none; }

    .ctl-stack {
      display: flex;
      flex-direction: column;
      gap: 8px;
      pointer-events: auto;
    }
    .ctl-stack button {
      width: 38px;
      height: 38px;
      display: grid;
      place-items: center;
      border-radius: var(--rk-r-pill);
      background: rgba(10, 14, 22, 0.55);
      backdrop-filter: blur(10px) saturate(140%);
      -webkit-backdrop-filter: blur(10px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.16);
      color: var(--rk-text);
      cursor: pointer;
      transition: background var(--rk-dur-fast),
        transform var(--rk-dur-fast) var(--rk-ease-spring),
        border-color var(--rk-dur-fast);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
    }
    .ctl-stack button:hover {
      background: rgba(10, 14, 22, 0.75);
      border-color: rgba(255, 255, 255, 0.32);
      transform: scale(1.06);
    }
    .ctl-stack button[active] {
      background: rgba(56, 189, 248, 0.25);
      color: var(--rk-brand-primary);
      border-color: var(--rk-border-brand);
    }
    .ctl-stack button svg { width: 16px; height: 16px; }

    /* Slotted into <rk-panel>'s .panel-foot when the HUD is docked there.
       Buttons inherit the same glass-button look. */
    .panel-controls {
      display: flex;
      gap: 8px;
      align-items: center;
      justify-content: center;
    }
    .panel-controls button {
      width: 40px;
      height: 40px;
      display: grid;
      place-items: center;
      border-radius: var(--rk-r-pill);
      background: rgba(10, 14, 22, 0.55);
      backdrop-filter: blur(10px) saturate(140%);
      -webkit-backdrop-filter: blur(10px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.16);
      color: var(--rk-text);
      cursor: pointer;
      transition: background var(--rk-dur-fast),
        transform var(--rk-dur-fast) var(--rk-ease-spring),
        border-color var(--rk-dur-fast);
    }
    .panel-controls button:hover {
      background: rgba(10, 14, 22, 0.75);
      border-color: rgba(255, 255, 255, 0.32);
      transform: scale(1.06);
    }
    .panel-controls button[active] {
      background: rgba(56, 189, 248, 0.25);
      color: var(--rk-brand-primary);
      border-color: var(--rk-border-brand);
    }
    .panel-controls button svg { width: 16px; height: 16px; }

    /* When the HUD is docked into the side panel, hide the entire bottom
       bar (HUD + flanking control stacks). The controls re-render inside
       the panel via slot, so the video stage is free of all chrome. */
    .bottom-bar[hidden] { display: none !important; }
    .bottom-bar rk-hud[hidden-overlay] { display: none; }

    /* ---- Mobile layout ----
       kvs20 mobile parity: hide the top header (driver strip, Multi-view,
       Switch links) and the side panel entirely. The video is full-bleed
       and the HUD overlays the bottom of the video stage.

       Catches both portrait phones (narrow) and landscape phones (short).
       A landscape phone is typically 844×390 — wider than 720 but shorter
       than 500, so the orientation+max-height clause picks it up while
       leaving iPads/laptops on the desktop layout. */
    @media (max-width: 720px),
           (orientation: landscape) and (max-height: 500px) {
      :host { grid-template-rows: 1fr; }
      header { display: none; }
      rk-panel { display: none; }
      .body { grid-template-columns: 1fr; }
      .stage { padding: 0; }
      rk-video { border-radius: 0; }

      /* kvs20 mobile parity: no overlay control buttons (swap, PiP, mute,
         fullscreen). The HUD becomes the sole content of the bottom bar.
         Hidden in both portrait and landscape — controls accessible only on
         desktop (≥721px wide or taller-than-500px landscape devices). */
      .ctl-stack { display: none; }

      /* kvs20 pattern: HUD card centred horizontally with a hard width cap.
         kvs20 used min(920px, calc(100% - 28px)) — capped at 920 with 14px
         outer margin. Centred via transform translate(-50%). On a phone in
         landscape (~844-932px wide) the cap means the HUD doesn't sprawl
         all the way to both edges; on a portrait phone it scales down to
         100% width minus the safe-area-aware margin. */
      .bottom-bar {
        bottom: calc(6px + env(safe-area-inset-bottom));
        padding-left: calc(14px + env(safe-area-inset-left));
        padding-right: calc(14px + env(safe-area-inset-right));
        gap: 0;
        justify-content: center;
      }
      .bottom-bar rk-hud {
        flex: 0 1 auto;
        width: 100%;
        max-width: 720px;
        display: block;
      }
    }
    /* Landscape phones — tighter bottom inset, matches kvs20 padY=4 */
    @media (orientation: landscape) and (max-height: 500px) {
      .bottom-bar {
        bottom: calc(4px + env(safe-area-inset-bottom));
      }
    }
  `;

  @property({ attribute: "logger-id" }) loggerId = "";

  @state() private driverName: string | null = null;
  @state() private panelOpen = true;
  @state() private hudDock: HudDock = "video";
  @state() private liveMode = this.resolveInitialLiveMode();

  @query("rk-video") private videoEl!: RkVideo;
  @query(".bottom-bar rk-hud") private hudEl!: RkHud;
  @query("rk-panel") private panelEl!: RkPanel;

  /** Gauges: always frame-locked to the playing picture (in-band ID3, with
   *  the DATERANGE fallback), whatever Team is set to. */
  private tele: TelemetryClient | null = null;
  /** Map only, while Team is on: position straight from the device feed.
   *  Team runs ahead of the video by the stream delay, which is right for
   *  following the car on the map but made the gauges disagree with the car
   *  in the picture when it drove them too. */
  private mapTele: TelemetryClient | null = null;

  private onKey = (ev: KeyboardEvent) => {
    if (ev.target instanceof HTMLInputElement || ev.target instanceof HTMLTextAreaElement) return;
    const k = ev.key.toLowerCase();
    if (k === "s" || k === "x") this.videoEl?.swapCamera();
    else if (k === "p") this.videoEl?.togglePip();
    else if (k === "f") this.videoEl?.toggleFullscreen();
    else if (k === "m") this.videoEl?.toggleMute();
  };

  /** When the tab returns from background, force an immediate telemetry
   *  poll so the panel timestamp + HUD values catch up without a manual
   *  refresh. Chrome throttles setInterval to 1 Hz on backgrounded tabs
   *  and may discard tabs entirely after long idle, so the regular poll
   *  isn't enough on its own. */
  private onVisibility = () => {
    if (document.visibilityState === "visible") { this.tele?.poke(); this.mapTele?.poke(); }
  };
  private onFocus = () => { this.tele?.poke(); this.mapTele?.poke(); };

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("keydown", this.onKey);
    document.addEventListener("visibilitychange", this.onVisibility);
    window.addEventListener("focus", this.onFocus);
    window.addEventListener("pageshow", this.onFocus);  // bfcache restore
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this.onKey);
    document.removeEventListener("visibilitychange", this.onVisibility);
    window.removeEventListener("focus", this.onFocus);
    window.removeEventListener("pageshow", this.onFocus);
    this.stopTelemetry();
  }

  protected firstUpdated(): void {
    this.mountTelemetry();
    this.loadDriver();
  }
  protected updated(changed: Map<string, unknown>): void {
    if (changed.has("loggerId")) {
      this.stopTelemetry();
      this.mountTelemetry();
      this.loadDriver();
    }
  }

  /** Resolved at mount; the source restored when Team (live) is toggled off. */
  private baseSource: TelemetrySource = "hlsInbandId3";

  /** Base telemetry source (the non-Team mode). Default is now "hlsInbandId3"
   *  (frame-locked telemetry muxed into the segments) with an automatic fall
   *  back to DATERANGE in TelemetryClient when a logger has no ID3 — so loggers
   *  whose box injector isn't running degrade gracefully instead of going dark.
   *  Force a path per-viewer with ?telsrc=daterange (legacy) or ?telsrc=inband. */
  /** Team (live) is ON by default: it takes position straight from the telemetry
   *  feed rather than reading it back out of the video, which is materially more
   *  accurate on track. The trade is that it is no longer frame-locked to the
   *  picture, so the marker can lead the video slightly. Anyone who explicitly
   *  asks for a base source with ?telsrc= gets that instead, with Team off. */
  private resolveInitialLiveMode(): boolean {
    return !(getQuery().get("telsrc") || "").trim();
  }

  private resolveBaseSource(): TelemetrySource {
    const v = (getQuery().get("telsrc") || "").trim();
    if (v === "inband") return "hlsInbandId3";
    if (v === "daterange" || v === "hls") return "hlsMetadata";
    if (isTelemetrySource(v)) return v;
    return "hlsInbandId3";
  }

  private mountTelemetry(): void {
    if (!this.loggerId) return;
    this.baseSource = this.resolveBaseSource();
    this.tele = new TelemetryClient({
      loggerId: this.loggerId,
      source: this.baseSource,
      // In-band ID3 telemetry for the playing frame (used when the base source
      // is "hlsInbandId3"). Frame-locked by the decoder — no window dependency.
      getInbandTelemetry: () => this.videoEl?.getInbandTelemetry() ?? null,
      // Step L4 (2026-06-04): TelemetryClient asks the video component
      // for the PDT of the playing frame, then picks the DATERANGE whose
      // START-DATE matches. Without this, the client would pick the
      // latest tag in the playlist (live edge) which leads the playing
      // frame by ~2 s — verified by stopwatch (4 s video) vs marker test
      // (2 s telemetry). With this wired, GPS marker / HUD speed / RPM
      // all line up with the car in the video frame.
      getVideoPdt: () => this.videoEl?.getCurrentPdt() ?? null,
      onUpdate: (snap) => {
        const t = Date.now();
        const videoLive = this.isVideoLive(t);
        // Always feed the overlay HUD so it stays warm when un-docked.
        this.hudEl?.feed(snap, t, videoLive);
        this.panelEl?.feedHud(snap);
        // With Team off the map follows the picture as well.
        if (!this.liveMode) this.panelEl?.feed(snap, videoLive);
      },
    });
    this.tele.start();
    this.syncMapTelemetry();
  }

  /** Stream is "live" if the VIDEO is current: the playing frame's PDT within
   *  ~20s of now (covers normal stream latency; a genuinely stalled video falls
   *  outside it, and the 13s stall watchdog reloads anyway). */
  private isVideoLive(t: number): boolean {
    const vpdt = this.videoEl?.getCurrentPdt() ?? null;
    return vpdt != null && t - vpdt < 20_000;
  }

  /** Start or stop the Team feed for the map to match the Team toggle. */
  private syncMapTelemetry(): void {
    if (this.liveMode && this.loggerId) {
      if (this.mapTele) return;
      this.mapTele = new TelemetryClient({
        loggerId: this.loggerId,
        source: "liveWebSocket",
        onUpdate: (snap) => this.panelEl?.feed(snap, this.isVideoLive(Date.now())),
      });
      this.mapTele.start();
    } else {
      this.mapTele?.stop();
      this.mapTele = null;
    }
  }

  private stopTelemetry(): void {
    this.tele?.stop();
    this.tele = null;
    this.mapTele?.stop();
    this.mapTele = null;
  }

  private async loadDriver(): Promise<void> {
    if (!this.loggerId) return;
    this.driverName = await getDriverName(this.loggerId);
  }

  private togglePanel = () => {
    this.panelOpen = !this.panelOpen;
  };

  private onHudDock = (ev: CustomEvent<HudDock>) => {
    this.hudDock = ev.detail;
  };

  private onLiveMode = (ev: CustomEvent<boolean>) => {
    const live = ev.detail;
    this.liveMode = live;
    // Team moves the map only; the gauges stay on the picture's telemetry.
    this.syncMapTelemetry();
  };

  // Mirror video state so the button [active] indicators reflect reality.
  @state() private pipOn = true;
  @state() private muted = true;

  private doSwap = () => this.videoEl?.swapCamera();
  private doTogglePip = () => {
    this.videoEl?.togglePip();
    this.pipOn = !this.pipOn;
  };
  private doToggleMute = () => {
    // Route through the video; our mirrored `muted` is updated by the
    // `mutechange` event it dispatches (single source of truth), so we
    // must NOT flip it here too or it double-toggles.
    this.videoEl?.toggleMute();
  };
  // Keep the mirrored state in sync with whichever control toggled mute
  // (in-video button, M key, or the bottom-bar button).
  private onMuteChange = (ev: CustomEvent<boolean>) => {
    this.muted = ev.detail;
  };
  private doFullscreen = () => this.videoEl?.toggleFullscreen();

  render() {
    const masked = this.loggerId.slice(-4);
    return html`
      <header>
        <span class="brand-spacer"></span>
        <div class="driver">
          ${this.driverName ? html`<b>${this.driverName}</b>` : ""}<span>***${masked}</span>
        </div>
        <div class="header-actions">
          <button @click=${this.togglePanel} title="Toggle details panel">
            ${this.panelOpen ? "Hide details" : "Show details"}
          </button>
          <a href="./race.html">Multi-view</a>
          <a href="./">⤺ Switch</a>
        </div>
      </header>
      <div class="body">
        <div class="stage">
          <rk-video logger-id=${this.loggerId} @mutechange=${this.onMuteChange}></rk-video>
          <div class="bottom-bar" ?hidden=${this.hudDock === "panel"}>
            <div class="ctl-stack">${this.renderCtl("swap")}${this.renderCtl("pip")}</div>
            <rk-hud variant="video"></rk-hud>
            <div class="ctl-stack">${this.renderCtl("mute")}${this.renderCtl("full")}</div>
          </div>
        </div>
        <rk-panel
          logger-id=${this.loggerId}
          ?open=${this.panelOpen}
          hud-dock=${this.hudDock}
          ?live-mode=${this.liveMode}
          @toggle=${this.togglePanel}
          @huddock=${this.onHudDock}
          @livemode=${this.onLiveMode}
        >
          ${this.hudDock === "panel"
            ? html`
                <div slot="panel-controls" class="panel-controls">
                  ${this.renderCtl("swap")}
                  ${this.renderCtl("pip")}
                  ${this.renderCtl("mute")}
                  ${this.renderCtl("full")}
                </div>
              `
            : ""}
        </rk-panel>
      </div>
    `;
  }

  /** Render one of the four video control buttons. Same markup whether it
   *  sits in the video stage bottom-bar or in the side panel's slot. */
  private renderCtl(kind: "swap" | "pip" | "mute" | "full") {
    switch (kind) {
      case "swap":
        return html`<button title="Swap cameras (S)" @click=${this.doSwap}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M7 4 3 8l4 4"/><path d="M3 8h13a4 4 0 0 1 4 4v0"/>
            <path d="m17 20 4-4-4-4"/><path d="M21 16H8a4 4 0 0 1-4-4v0"/>
          </svg>
        </button>`;
      case "pip":
        return html`<button title="Toggle PiP (P)" @click=${this.doTogglePip} ?active=${this.pipOn}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2"/>
            <rect x="13" y="11" width="6" height="5" rx="1" fill="currentColor"/>
          </svg>
        </button>`;
      case "mute":
        return html`<button title="Mute (M)" @click=${this.doToggleMute} ?active=${!this.muted}>
          ${this.muted
            ? html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4Z"/><line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/></svg>`
            : html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4Z"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>`}
        </button>`;
      case "full":
        return html`<button title="Fullscreen (F)" @click=${this.doFullscreen}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
            <path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
          </svg>
        </button>`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rk-player": RkPlayer;
  }
}
